import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTheme } from '../../hooks/useTheme';
import type { SandboxProfile } from '../../types/sandbox';

interface NavbarProps {
  profile: SandboxProfile | null;
  onLogout: () => void;
}

const NOTIFICATIONS = [
  { emoji: '🏆', text: 'Agent earned "First Ping"' },
  { emoji: '💬', text: 'New comment in HVAC Group' },
  { emoji: '🤖', text: 'Sym-Agent completed a task' },
];

export function Navbar({ profile, onLogout }: NavbarProps) {
  const navigate = useNavigate();
  const { toggle } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      navigate(`/discover?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  return (
    <nav
      className="h-[34px] flex items-center justify-between px-4 sticky top-0 z-50 flex-shrink-0"
      style={{ background: 'var(--bg-nav)', borderBottom: '1px solid var(--accent-hover)' }}
    >
      <div className="flex items-center gap-4">
        {/* Logo */}
        <Link
          to="/"
          className="text-sm font-bold font-mono px-1 no-underline"
          style={{ background: 'white', color: 'var(--accent)' }}
        >
          [cb]
        </Link>

        {/* Nav links */}
        <div className="hidden md:flex gap-4 text-xs font-bold">
          <NavLink to="/" end className="no-underline" style={{ color: 'var(--text-nav)' }}>Feed</NavLink>
          <NavLink to="/discover" className="no-underline" style={{ color: 'var(--text-nav)' }}>Discover</NavLink>
          <NavLink to="/groups" className="no-underline" style={{ color: 'var(--text-nav)' }}>Groups</NavLink>
          <NavLink to="/marketplace" className="no-underline" style={{ color: 'var(--text-nav)' }}>Marketplace</NavLink>
          <NavLink to="/graph" className="no-underline" style={{ color: 'var(--text-nav)' }}>Graph</NavLink>
          <NavLink to="/leaderboard" className="no-underline" style={{ color: 'var(--text-nav)' }}>Leaderboard</NavLink>
          <NavLink to="/store" className="no-underline" style={{ color: '#ffd700' }}>Shop</NavLink>
          <NavLink to="/premium" className="no-underline" style={{ color: '#ffd700' }}>Premium</NavLink>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Search */}
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onKeyDown={handleSearch}
          placeholder="Search..."
          className="h-5 text-xs px-1 rounded-sm w-32 focus:w-48 transition-all focus:outline-none"
          style={{ border: '1px solid var(--accent-hover)' }}
        />

        {/* Notifications */}
        {profile && (
          <div className="relative cursor-pointer" onClick={() => { setShowNotifs(!showNotifs); setShowMenu(false); }}>
            <span className="text-lg">🔔</span>
            <span className="nav-badge">3</span>

            {showNotifs && (
              <>
                <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setShowNotifs(false); }} />
                <div className="absolute right-0 top-6 w-48 social-box shadow-lg z-50" style={{ fontSize: '10px' }}>
                  {NOTIFICATIONS.map((n, i) => (
                    <div
                      key={i}
                      className="p-2 cursor-pointer"
                      style={{ borderBottom: i < NOTIFICATIONS.length - 1 ? '1px solid var(--border-separator)' : 'none' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      {n.emoji} {n.text}
                    </div>
                  ))}
                  <Link
                    to="/notifications"
                    onClick={() => setShowNotifs(false)}
                    className="block p-2 text-center font-bold no-underline"
                    style={{ borderTop: '1px solid var(--border-separator)', color: 'var(--text-link)' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    View All Notifications
                  </Link>
                </div>
              </>
            )}
          </div>
        )}

        {/* Theme toggle */}
        <button
          onClick={toggle}
          className="text-xs opacity-80 hover:opacity-100"
          style={{ color: 'var(--text-nav)' }}
        >
          ◑
        </button>

        {/* User avatar / Sign in */}
        {profile ? (
          <div className="relative">
            <div
              className="w-5 h-5 border overflow-hidden cursor-pointer flex items-center justify-center text-white font-bold"
              style={{ background: 'linear-gradient(135deg, #374151, #000)', borderColor: 'white', fontSize: '8px' }}
              onClick={() => { setShowMenu(!showMenu); setShowNotifs(false); }}
            >
              {(profile.display_name || profile.username).charAt(0).toUpperCase()}
            </div>

            {showMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-full mt-1 w-40 social-box shadow-lg z-50 animate-fade-in" style={{ fontSize: '10px' }}>
                  <Link
                    to={`/@${profile.username}`}
                    onClick={() => setShowMenu(false)}
                    className="block px-3 py-2 no-underline"
                    style={{ color: 'var(--text-primary)' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    👤 My Profile
                  </Link>
                  <Link
                    to="/messages"
                    onClick={() => setShowMenu(false)}
                    className="block px-3 py-2 no-underline"
                    style={{ color: 'var(--text-primary)', borderTop: '1px solid var(--border-separator)' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    💬 Messages
                  </Link>
                  <Link
                    to="/settings"
                    onClick={() => setShowMenu(false)}
                    className="block px-3 py-2 no-underline"
                    style={{ color: 'var(--text-primary)', borderTop: '1px solid var(--border-separator)' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    ⚙️ Settings
                  </Link>
                  <button
                    onClick={() => { onLogout(); setShowMenu(false); }}
                    className="block w-full text-left px-3 py-2"
                    style={{ color: 'var(--text-primary)', borderTop: '1px solid var(--border-separator)', background: 'transparent' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    🚪 Log Out
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <button
            onClick={() => navigate('/login')}
            className="text-xs font-bold px-2 py-0.5"
            style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--text-nav)', border: '1px solid rgba(255,255,255,0.3)' }}
          >
            Sign In
          </button>
        )}
      </div>
    </nav>
  );
}
